import "../Tenant/tenants.css";

import { useMutation, useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useSnackbar } from "material-ui-snackbar-provider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";

import AlertDialog from "../../components/utils/alertDialog";
import { SessionHelper } from "../../utils/index";

const GET_PLATFORMS = gql`
  query PlatformSource {
    platforms {
      id
      value
      status
      label
    }
  }
`;

const DELETE_PLATFORMSOURCE = gql`
  mutation PlatformSourceDelete($id: ID!) {
    platformDelete(id: $id)
  }
`;

// ── SVG icons ──────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const ExportIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const EmptyIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z" />
    <path d="M12 8v4l3 3" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

// ── Dropdown portal ────────────────────────────────────────
function DropdownMenu({ pos, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div ref={ref} className="dropdown-menu" style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 9999 }}>
      {children}
    </div>,
    document.body
  );
}

// ── Main component ─────────────────────────────────────────
function SourceList() {
  const { data = {}, loading } = useQuery(GET_PLATFORMS, { fetchPolicy: "network-only" });
  const [platformDelete] = useMutation(DELETE_PLATFORMSOURCE);
  const snackbar = useSnackbar();

  const [alertOpen, setAlertOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  const history = useHistory();
  const currentUsername = SessionHelper?.currentUser?.username;
  const canEdit = currentUsername !== "ICQ24";

  const allSources = data.platforms || [];

  const filtered = allSources.filter((s) => {
    const q = searchQuery.toLowerCase();
    return (
      (s.value || "").toLowerCase().includes(q) ||
      (s.label || "").toLowerCase().includes(q)
    );
  });

  const totalCount = filtered.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const paginated = filtered.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);

  const handleMenuOpen = useCallback((id, e) => {
    e.stopPropagation();
    if (openMenuId === id) { setOpenMenuId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenMenuId(id);
  }, [openMenuId]);

  const closeMenu = useCallback(() => setOpenMenuId(null), []);

  const handleDeleteClick = (id) => {
    closeMenu();
    setDeleteId(id);
    setAlertOpen(true);
  };

  const alertOption = (confirmed) => {
    setAlertOpen(false);
    if (confirmed && deleteId) {
      platformDelete({
        variables: { id: deleteId },
        refetchQueries: [{ query: GET_PLATFORMS }],
        awaitRefetchQueries: true,
      })
        .then(() => snackbar.showMessage("Source deleted"))
        .catch(console.error);
      setDeleteId(null);
    }
  };

  const handleExport = () => {
    const headers = ["Source", "Label", "Status"];
    const rows = filtered.map((s) => [s.value || "", s.label || "", s.status ? "Active" : "Inactive"]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "sources.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const activeSource = paginated.find((s) => s.id === openMenuId);
  const firstRow = safePage * rowsPerPage + 1;
  const lastRow = Math.min((safePage + 1) * rowsPerPage, totalCount);

  return (
    <div className="tenants-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">Sources</h1>
          <p className="page-subtitle">Manage platform sources across your organization.</p>
        </div>
        {canEdit && (
          <button className="btn-primary" onClick={() => history.push("/SourceCreate")}>
            + New Source
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <div className="search-container">
            <span className="search-icon"><SearchIcon /></span>
            <input
              className="search-input"
              type="text"
              placeholder="Search by source or label..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            />
          </div>
          <button className="btn-outline"><FilterIcon /> Filter</button>
          <button className="btn-outline" onClick={handleExport}><ExportIcon /> Export</button>
        </div>
        <span className="tenant-count">{allSources.length} Sources</span>
      </div>

      {/* Table card */}
      <div className="table-card">
        {loading ? (
          <div className="loading-state">
            <span className="loading-spinner" />Loading sources…
          </div>
        ) : allSources.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon"><EmptyIcon /></span>
            <h3 className="empty-state-title">No sources found</h3>
            <p className="empty-state-desc">Create your first platform source to get started.</p>
            {canEdit && (
              <button className="btn-primary" onClick={() => history.push("/SourceCreate")}>
                New Source
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="tenants-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Label</th>
                    <th>Status</th>
                    {canEdit && <th className="actions-cell" />}
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 4 : 3} style={{ textAlign: "center", padding: "48px", color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
                        No results for &ldquo;{searchQuery}&rdquo;
                      </td>
                    </tr>
                  ) : paginated.map((source) => (
                    <tr key={source.id}>
                      <td>
                        <span className="domain-id-badge">{source.value}</span>
                      </td>
                      <td>
                        <span className="domain-name">{source.label}</span>
                      </td>
                      <td>
                        <span className={`status-pill ${source.status ? "status-active" : "status-inactive"}`}>
                          <span className="status-dot" />
                          {source.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {canEdit && (
                        <td className="actions-cell">
                          <div className="actions-menu-wrapper">
                            <button className="menu-trigger" onClick={(e) => handleMenuOpen(source.id, e)} aria-label="Open actions">
                              <MoreIcon />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <div className="rows-per-page">
                <span>Rows per page</span>
                <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
              </div>
              <div className="pagination-controls">
                <span className="pagination-info">
                  {totalCount === 0 ? "0" : `${firstRow}–${lastRow}`} of {totalCount}
                </span>
                <button className="pagination-btn" disabled={safePage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} aria-label="Previous page">
                  <ChevronLeftIcon />
                </button>
                <span className="pagination-current">{safePage + 1} / {pageCount}</span>
                <button className="pagination-btn" disabled={safePage >= pageCount - 1} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Dropdown portal */}
      {openMenuId !== null && activeSource && (
        <DropdownMenu pos={menuPos} onClose={closeMenu}>
          <button className="dropdown-item" onClick={() => { closeMenu(); history.push(`/SourceEdit/${activeSource.id}`); }}>
            Edit Source
          </button>
          <div className="dropdown-divider" />
          <button className="dropdown-item dropdown-item-danger" onClick={() => handleDeleteClick(activeSource.id)}>
            Delete Source
          </button>
        </DropdownMenu>
      )}

      <AlertDialog open={alertOpen} alertOption={alertOption} />
    </div>
  );
}

export default SourceList;
